/**
 * The benchmark's figures (P2.0-T4): what one row of the results table holds, worked out from the frames recorded
 * while the row was measured. Pure, so the same frames give the same row on any machine and in tests.
 */

/**
 * A frame slower than this, in milliseconds, is a stutter: two frames of a 60 Hz display.
 */
export const STUTTER_MS = 33;

/**
 * How long after the loaded chunks change a frame counts toward the crossing's stutter, in milliseconds.
 */
export const CROSSING_WINDOW_MS = 1_000;

/**
 * What a row recorded: each frame's time since the one before, when it ended and the chunk the camera's centre stood in
 * (`chunkKey`), the times at which the loaded chunk range changed, and the most props and creatures drawn on any frame.
 */
export interface RowLog {
  readonly frameMs: number[];
  readonly endedAt: number[];
  readonly chunks: number[];
  readonly crossingsAt: number[];
  props: number;
  creatures: number;
}

/**
 * One row of the results table.
 */
export interface RowFigures {
  readonly id: string;
  readonly frames: number;
  readonly seconds: number;
  readonly fps: number;
  readonly p50: number;
  readonly p95: number;
  readonly p99: number;
  readonly longest: number;
  readonly over33: number;
  readonly crossings: number;
  readonly crossingOver33: number;
  readonly crossingLongest: number;
  readonly props: number;
  readonly creatures: number;
  readonly note: string;
}

/**
 * An empty log.
 *
 * @returns The log.
 */
export function newRowLog(): RowLog {
  return { frameMs: [], endedAt: [], chunks: [], crossingsAt: [], props: 0, creatures: 0 };
}

/**
 * The value at a fraction of a sorted list, by nearest rank.
 *
 * @param sorted Values in ascending order.
 * @param fraction Rank as a fraction, from 0 to 1.
 * @returns The value, or 0 for an empty list.
 */
export function nearestRank(sorted: readonly number[], fraction: number): number {
  if (sorted.length === 0) {
    return 0;
  }
  const rank = Math.min(sorted.length, Math.max(1, Math.ceil(fraction * sorted.length)));
  return sorted[rank - 1] as number;
}

/**
 * Rounds to one decimal place.
 *
 * @param value The value.
 * @returns The rounded value.
 */
function tenth(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Works a row's figures out of its log: frames per second over the frames' own time, the frame time percentiles and
 * the longest frame, the frames over `STUTTER_MS`, and of those the ones ending within `CROSSING_WINDOW_MS` after a
 * chunk range change, with the longest frame in those windows.
 *
 * @param id The row's id.
 * @param log What it recorded.
 * @param note A remark for the row, or "".
 * @returns The figures, times in milliseconds to a tenth.
 */
export function rowFigures(id: string, log: RowLog, note = ""): RowFigures {
  const sorted = [...log.frameMs].sort((a, b) => a - b);
  const total = log.frameMs.reduce((sum, ms) => sum + ms, 0);
  let crossingOver33 = 0;
  let crossingLongest = 0;
  log.frameMs.forEach((ms, index) => {
    const ended = log.endedAt[index] as number;
    if (log.crossingsAt.some((at) => ended >= at && ended - at <= CROSSING_WINDOW_MS)) {
      crossingLongest = Math.max(crossingLongest, ms);
      crossingOver33 += ms > STUTTER_MS ? 1 : 0;
    }
  });
  return {
    id,
    frames: log.frameMs.length,
    seconds: tenth(total / 1000),
    fps: total > 0 ? tenth((log.frameMs.length * 1000) / total) : 0,
    p50: tenth(nearestRank(sorted, 0.5)),
    p95: tenth(nearestRank(sorted, 0.95)),
    p99: tenth(nearestRank(sorted, 0.99)),
    longest: tenth(sorted[sorted.length - 1] ?? 0),
    over33: log.frameMs.filter((ms) => ms > STUTTER_MS).length,
    crossings: log.crossingsAt.length,
    crossingOver33,
    crossingLongest: tenth(crossingLongest),
    props: log.props,
    creatures: log.creatures,
    note,
  };
}

/**
 * A chunk as one number, for the per-chunk figures.
 *
 * @param cx Chunk column, 0 to 65,535.
 * @param cy Chunk row, 0 to 65,535.
 * @returns The key.
 */
export function chunkKey(cx: number, cy: number): number {
  return cx * 65536 + cy;
}

/**
 * The frames measured while the camera's centre stood in one chunk, over every row: where a report finds a map's
 * weak spots.
 */
export interface ChunkFigures {
  readonly cx: number;
  readonly cy: number;
  readonly frames: number;
  readonly p50: number;
  readonly p95: number;
  readonly longest: number;
  readonly over33: number;
}

/**
 * The per-chunk figures of a run, slowest first by the 95th percentile, then the longest frame, then position.
 *
 * @param logs Every row's log.
 * @returns The chunks.
 */
export function chunkFigures(logs: readonly RowLog[]): ChunkFigures[] {
  const byChunk = new Map<number, number[]>();
  for (const log of logs) {
    log.frameMs.forEach((ms, index) => {
      const key = log.chunks[index] as number;
      let list = byChunk.get(key);
      if (list === undefined) {
        list = [];
        byChunk.set(key, list);
      }
      list.push(ms);
    });
  }
  return [...byChunk]
    .map(([key, frames]): ChunkFigures => {
      const sorted = [...frames].sort((a, b) => a - b);
      return { cx: Math.floor(key / 65536), cy: key % 65536, frames: frames.length, p50: tenth(nearestRank(sorted, 0.5)), p95: tenth(nearestRank(sorted, 0.95)), longest: tenth(sorted[sorted.length - 1] ?? 0), over33: frames.filter((ms) => ms > STUTTER_MS).length };
    })
    .sort((a, b) => b.p95 - a.p95 || b.longest - a.longest || a.cx - b.cx || a.cy - b.cy);
}
